import { getDemoLineValidations } from '../data/inventoryDemoDataset';

const STORAGE_KEY = 'poc-app-inventory-mock-line-validations-v1';

export interface MockLineValidationEntry {
  quantiteComptee: string;
  comptePar?: string;
}

interface ValidationStorageShape {
  byReference: Record<string, Record<string, MockLineValidationEntry>>;
}

const emptyShape = (): ValidationStorageShape => ({ byReference: {} });

function normalizeByReference(
  input: unknown,
): { byReference: ValidationStorageShape['byReference']; migrated: boolean } {
  if (!input || typeof input !== 'object') {
    return { byReference: {}, migrated: false };
  }

  let migrated = false;
  const out: ValidationStorageShape['byReference'] = {};

  for (const [ref, val] of Object.entries(input as Record<string, unknown>)) {
    if (Array.isArray(val)) {
      migrated = true;
      const map: Record<string, MockLineValidationEntry> = {};

      for (const id of val) {
        if (typeof id === 'string') {
          map[id] = { quantiteComptee: '' };
        }
      }

      out[ref] = map;
    } else if (val && typeof val === 'object') {
      const map: Record<string, MockLineValidationEntry> = {};

      for (const [lineId, entry] of Object.entries(val as Record<string, unknown>)) {
        if (entry && typeof entry === 'object' && 'quantiteComptee' in entry) {
          const obj = entry as { quantiteComptee: unknown; comptePar?: unknown };
          const q = obj.quantiteComptee;
          const comptePar = obj.comptePar;

          map[lineId] = {
            quantiteComptee: typeof q === 'string' ? q : String(q ?? ''),
            ...(typeof comptePar === 'string' && comptePar.trim() !== ''
              ? { comptePar: comptePar.trim() }
              : {}),
          };
        } else if (typeof entry === 'string') {
          map[lineId] = { quantiteComptee: entry };
        }
      }

      out[ref] = map;
    } else {
      out[ref] = {};
    }
  }

  return { byReference: out, migrated };
}

function writeStorage(data: ValidationStorageShape): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function readStorage(): ValidationStorageShape {
  if (typeof window === 'undefined') {
    return emptyShape();
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return emptyShape();
    }

    const parsed = JSON.parse(raw) as unknown;

    if (!parsed || typeof parsed !== 'object') {
      return emptyShape();
    }

    const obj = parsed as { byReference?: unknown };

    if (!obj.byReference || typeof obj.byReference !== 'object') {
      return emptyShape();
    }

    const { byReference, migrated } = normalizeByReference(obj.byReference);

    if (migrated) {
      writeStorage({ byReference });
    }

    return { byReference };
  } catch {
    return emptyShape();
  }
}

/** Lignes mock validées pour un inventaire : id de ligne → quantité comptée persistée. */
export function loadMockLineValidationsForInventory(
  referenceInventaire: string,
): Record<string, MockLineValidationEntry> {
  const data = readStorage();
  const persisted = data.byReference[referenceInventaire] ?? {};
  const demo = getDemoLineValidations(referenceInventaire);

  if (!demo) {
    return persisted;
  }

  return { ...demo, ...persisted };
}

/** @deprecated Utiliser loadMockLineValidationsForInventory ; conservé pour compatibilité éventuelle. */
export function loadValidatedMockLineIdsForInventory(referenceInventaire: string): Set<string> {
  const map = loadMockLineValidationsForInventory(referenceInventaire);

  return new Set(Object.keys(map));
}

export function persistMockLineComptageValide(
  referenceInventaire: string,
  lineId: string,
  quantiteComptee: string,
  comptePar: string,
): void {
  const data = readStorage();
  const existing = data.byReference[referenceInventaire] ?? {};

  data.byReference[referenceInventaire] = {
    ...existing,
    [lineId]: { quantiteComptee, comptePar },
  };
  writeStorage(data);
}
